import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { SBLogo } from "../components/WebLayout";
import Seo from "../components/Seo";
import { supabase, getAccessToken } from "../lib/supabaseClient";
import { toast } from "sonner";
import { formatEnum } from "../lib/labels";
import {
  LayoutGrid, Users, Layers, Building2, Store, LogOut, TrendingUp, Loader2,
  Download, RefreshCw, Mail as MailIcon, Search, Video, MessageSquare, Gauge,
  Trash2, AlertTriangle, ArrowRight, Send, Plus, X, Sparkles, CheckCircle2,
  ShieldOff, Clock, Link2, MapPin, Upload,
} from "lucide-react";
import AdminLocationsSection from "./AdminLocationsSection";
import AdminOperatorMarketplace from "./AdminOperatorMarketplace";

const API = process.env.REACT_APP_BACKEND_URL || "";

async function authedFetch(path, opts = {}) {
  const token = await getAccessToken();
  const headers = { ...(opts.headers || {}), Authorization: `Bearer ${token || ""}` };
  return fetch(`${API}${path}`, { ...opts, headers });
}

// Sidebar sections. `section` is what drives the main-area render.
// launch-readiness routes to a separate page. logout runs signOut.
const SIDEBAR = [
  { key: "overview",     label: "Overview",           icon: LayoutGrid },
  { key: "locations",    label: "Vending Locations",  icon: MapPin },
  { key: "marketplace",  label: "Operator Marketplace", icon: Store },
  { key: "reg:scouts",   label: "Scouts",             icon: Users },
  { key: "reg:operators",label: "Operators",          icon: Layers },
  { key: "reg:businesses", label: "Businesses",       icon: Building2 },
  { key: "reg:creators", label: "Creators",           icon: Video },
  { key: "sellers",      label: "Seller Interests",   icon: Store },
  { key: "outreach",     label: "Outreach",           icon: Send },
  { key: "search",       label: "Registration Search",icon: Search },
  { key: "contact",      label: "Contact Messages",   icon: MessageSquare },
  { key: "launch",       label: "Launch Readiness",   icon: Gauge, route: "/admin/launch-readiness" },
  { key: "logout",       label: "Log Out",            icon: LogOut, action: "logout" },
];

const KIND_FROM_REG = { "reg:scouts": "scouts", "reg:operators": "operators", "reg:businesses": "businesses", "reg:creators": "creators" };

export default function AdminDashboard() {
  const nav = useNavigate();
  const [section, setSection] = useState("overview");
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    (async () => {
      // If a password recovery is active, force the reset screen instead of loading dashboard.
      try {
        if (sessionStorage.getItem("spotbounty_password_recovery_active") === "true") {
          window.location.replace("/auth/reset-password"); return;
        }
      } catch { /* ignore */ }
      const r = await authedFetch("/api/admin/stats/overview");
      if (!r.ok) { nav("/admin/login"); return; }
      setAuthChecked(true);
    })();
  }, [nav]);

  async function logout() {
    try { await supabase.auth.signOut(); } catch { /* ignore */ }
    nav("/admin/login");
  }

  function onSidebarClick(item) {
    if (item.action === "logout") return logout();
    if (item.route) return nav(item.route);
    setSection(item.key);
  }

  if (!authChecked) {
    return (
      <div className="min-h-screen bg-[#080B0D] text-white flex items-center justify-center">
        <div className="flex items-center gap-3 text-[#A1A1AA]"><Loader2 size={18} className="animate-spin text-[#FFC107]" /> Loading admin…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080B0D] text-white flex">
      <Seo title="Admin Dashboard" description="SpotBounty owner administration." path="/admin" noindex />

      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-60 border-r border-[#2A2F34] bg-[#0B0E11] p-5 sticky top-0 h-screen overflow-y-auto sb-scroll" data-testid="admin-sidebar">
        <Link to="/"><SBLogo size={18} /></Link>
        <div className="mt-8 sb-eyebrow">Admin</div>
        <nav className="mt-3 space-y-1">
          {SIDEBAR.map((n) => {
            const Icon = n.icon;
            const active = section === n.key;
            return (
              <button
                key={n.key}
                onClick={() => onSidebarClick(n)}
                data-testid={`admin-nav-${n.key}`}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] transition-colors ${
                  active ? "bg-[#FFC107]/10 text-[#FFC107]" : "text-[#A1A1AA] hover:text-white hover:bg-white/5"
                }`}
              >
                <Icon size={16} /> {n.label}
              </button>
            );
          })}
        </nav>
      </aside>

      <div className="flex-1 min-w-0 p-6 space-y-6">
        {/* Mobile section switcher — visible below lg */}
        <div className="lg:hidden flex items-center gap-2">
          <select
            value={section}
            onChange={(e) => setSection(e.target.value)}
            className="sb-input flex-1"
            data-testid="admin-mobile-section"
          >
            {SIDEBAR.filter((n) => !n.route && n.action !== "logout").map((n) => (
              <option key={n.key} value={n.key}>{n.label}</option>
            ))}
          </select>
          <button onClick={() => nav("/admin/launch-readiness")} className="sb-btn-secondary text-[11px]" style={{ padding: "10px 12px" }} data-testid="admin-mobile-launch">
            <Gauge size={12} /> Launch
          </button>
          <button onClick={logout} className="sb-btn-secondary text-[11px]" style={{ padding: "10px 12px" }} data-testid="admin-mobile-logout">
            <LogOut size={12} />
          </button>
        </div>

        {section === "overview" && <OverviewSection />}
        {section === "locations" && <AdminLocationsSection />}
        {section === "marketplace" && <AdminOperatorMarketplace />}
        {section in KIND_FROM_REG && <RegistrationsSearch initialKind={KIND_FROM_REG[section]} lockKind />}
        {section === "sellers" && <SellerInterestsSection />}
        {section === "outreach" && <OutreachSection />}
        {section === "search" && <RegistrationsSearch />}
        {section === "contact" && <ContactMessagesSection />}
      </div>
    </div>
  );
}

// -------------------------------------------------------------------------
// OVERVIEW — real DB data only. No hardcoded stats, no fabricated pins.
// -------------------------------------------------------------------------

function OverviewSection() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [recent, setRecent] = useState([]);
  const [topReferrals, setTopReferrals] = useState([]);
  const [pendingEmails, setPendingEmails] = useState({ scouts: 0, operators: 0, businesses: 0, creators: 0 });
  const [geo, setGeo] = useState({ states: [], top_cities: [] });
  const [demand, setDemand] = useState([]);
  const [busy, setBusy] = useState({});

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, rc, tr, pe, gg, dm] = await Promise.all([
        authedFetch("/api/admin/stats/overview").then((r) => r.ok ? r.json() : null),
        authedFetch("/api/admin/recent-signups?limit=20").then((r) => r.ok ? r.json() : { rows: [] }),
        authedFetch("/api/admin/referrals/top?limit=10").then((r) => r.ok ? r.json() : { rows: [] }),
        authedFetch("/api/admin/pending-emails").then((r) => r.ok ? r.json() : {}),
        authedFetch("/api/admin/geo").then((r) => r.ok ? r.json() : { states: [], top_cities: [] }),
        authedFetch("/api/admin/operator-demand").then((r) => r.ok ? r.json() : { rows: [] }),
      ]);
      if (!ov) { nav("/admin/login"); return; }
      setOverview(ov);
      setRecent(rc.rows || []);
      setTopReferrals(tr.rows || []);
      setPendingEmails(pe || {});
      setGeo(gg || { states: [], top_cities: [] });
      setDemand((dm && dm.rows) || []);
    } catch {
      nav("/admin/login");
    } finally { setLoading(false); }
  }, [nav]);

  useEffect(() => { loadAll(); }, [loadAll]);

  async function downloadCsv(kind) {
    setBusy((b) => ({ ...b, [`csv-${kind}`]: true }));
    try {
      const r = await authedFetch(`/api/admin/export/${kind}.csv`);
      if (!r.ok) throw new Error("export failed");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `spotbounty-${kind}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("CSV export failed"); }
    finally { setBusy((b) => ({ ...b, [`csv-${kind}`]: false })); }
  }
  async function downloadFullZip() {
    setBusy((b) => ({ ...b, zip: true }));
    try {
      const r = await authedFetch("/api/admin/export/full.zip");
      if (!r.ok) throw new Error("export failed");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "spotbounty-waitlist.zip"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Full export failed"); }
    finally { setBusy((b) => ({ ...b, zip: false })); }
  }
  async function retryEmails(userType) {
    setBusy((b) => ({ ...b, [`retry-${userType}`]: true }));
    try {
      const r = await authedFetch(`/api/admin/emails/retry`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_type: userType }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(data?.detail || "Retry failed"); return; }
      toast.success(`Retried ${data.attempted || 0}, sent ${data.sent || 0}`);
      loadAll();
    } catch { toast.error("Retry failed"); }
    finally { setBusy((b) => ({ ...b, [`retry-${userType}`]: false })); }
  }

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="flex items-center gap-3 text-[#A1A1AA]"><Loader2 size={18} className="animate-spin text-[#FFC107]" /> Loading overview…</div>
      </div>
    );
  }

  const stats = overview ? [
    { label: "Scouts",       value: overview.scouts.total },
    { label: "Operators",    value: overview.operators.total },
    { label: "Businesses",   value: overview.businesses.total },
    { label: "Creators",     value: overview.creators.total },
    { label: "Sellers",      value: (overview.sellers && overview.sellers.total) || 0 },
    { label: "Last 7d",      value: overview.scouts.last_7d + overview.operators.last_7d + overview.businesses.last_7d + overview.creators.last_7d + (overview.sellers?.last_7d || 0) },
    { label: "Last 30d",     value: overview.scouts.last_30d + overview.operators.last_30d + overview.businesses.last_30d + overview.creators.last_30d + (overview.sellers?.last_30d || 0) },
    { label: "Pending Emails", value: (pendingEmails.scouts + pendingEmails.operators + pendingEmails.businesses + pendingEmails.creators) || 0, tone: "amber" },
    { label: "Referral Codes", value: topReferrals.length },
  ] : [];

  return (
    <>
      <header className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="sb-eyebrow">System Health</div>
          <h1 className="mt-1 sb-headline text-[40px] md:text-[52px]">
            SPOTBOUNTY IS <span className="text-[#FFC107]">HEALTHY.</span>
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={loadAll} data-testid="admin-refresh" className="sb-btn-secondary text-[11px]" style={{ padding: "10px 14px" }}>
            <RefreshCw size={13} /> Refresh
          </button>
        </div>
      </header>

      <section className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-9 gap-3" data-testid="admin-stats">
        {stats.map((s) => (
          <div key={s.label} className="sb-card p-4" data-testid={`admin-stat-${s.label.toLowerCase().replace(/ /g,'-')}`}>
            <div className="sb-eyebrow" style={{ fontSize: 9 }}>{s.label}</div>
            <div className={`mt-2 text-[18px] font-black ${s.tone === "amber" ? "text-[#F59E0B]" : "text-white"}`}>{s.value}</div>
          </div>
        ))}
      </section>

      <section className="sb-card p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="sb-eyebrow">Data Operations</div>
            <div className="mt-1 text-[14px] text-[#A1A1AA]">Waitlist exports (CSV per table + full ZIP bundle) and email delivery retries.</div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {["scouts", "operators", "businesses", "creators"].map((k) => (
              <button key={k} onClick={() => downloadCsv(k)} disabled={busy[`csv-${k}`]} data-testid={`admin-export-${k}`} className="sb-btn-secondary text-[11px]" style={{ padding: "8px 12px" }}>
                {busy[`csv-${k}`] ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} {k}.csv
              </button>
            ))}
            <button onClick={downloadFullZip} disabled={busy.zip} data-testid="admin-export-zip" className="sb-btn-primary text-[11px]" style={{ padding: "8px 14px" }}>
              {busy.zip ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} Full ZIP
            </button>
          </div>
        </div>
        <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {["scouts", "operators", "businesses", "creators"].map((k) => (
            <div key={k} className="sb-card p-4 flex items-center justify-between">
              <div>
                <div className="sb-eyebrow" style={{ fontSize: 9 }}>{k} · pending emails</div>
                <div className="mt-1 text-[20px] font-black">{pendingEmails[k] || 0}</div>
              </div>
              <button onClick={() => retryEmails(k)} disabled={busy[`retry-${k}`] || !pendingEmails[k]} data-testid={`admin-retry-${k}`} className="sb-btn-secondary text-[10px]" style={{ padding: "7px 10px" }}>
                {busy[`retry-${k}`] ? <Loader2 size={11} className="animate-spin" /> : <MailIcon size={11} />} Retry
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* Real geographic breakdown — no synthetic pins, no hardcoded map coords. */}
      <section className="grid lg:grid-cols-2 gap-6">
        <div className="sb-card p-6">
          <div className="sb-eyebrow flex items-center gap-2"><TrendingUp size={12} className="text-[#FFC107]" /> Signups by State · Real Data</div>
          <div className="mt-4 divide-y divide-[#2A2F34]" data-testid="admin-geo-states">
            {(geo.states || []).slice(0, 12).map((s) => (
              <div key={s.state} className="py-2 flex items-center justify-between text-[12.5px]">
                <div className="font-bold">{s.state}</div>
                <div className="flex gap-3 text-[11px] text-[#A1A1AA]">
                  <span>Scouts <b className="text-white">{s.scouts}</b></span>
                  <span>Ops <b className="text-white">{s.operators}</b></span>
                  <span>Biz <b className="text-white">{s.businesses}</b></span>
                </div>
              </div>
            ))}
            {(!geo.states || geo.states.length === 0) && <div className="py-4 text-[13px] text-[#A1A1AA]">No geographic data yet.</div>}
          </div>
        </div>
        <div className="sb-card p-6">
          <div className="sb-eyebrow">Top Cities · Real Data</div>
          <div className="mt-4 divide-y divide-[#2A2F34]" data-testid="admin-geo-cities">
            {(geo.top_cities || []).slice(0, 12).map((c) => (
              <div key={`${c.city}-${c.state}`} className="py-3 flex items-center justify-between">
                <div>
                  <div className="text-[13px] font-bold">{c.city}, {c.state}</div>
                  <div className="text-[11px] text-[#A1A1AA]">{c.total} total signups</div>
                </div>
                <div className="text-[14px] font-black text-[#FFC107]">{c.total}</div>
              </div>
            ))}
            {(!geo.top_cities || geo.top_cities.length === 0) && <div className="py-4 text-[13px] text-[#A1A1AA]">No city data yet.</div>}
          </div>
        </div>
      </section>

      <section className="sb-card p-6">
        <div className="sb-eyebrow">Operator Demand Signal · Real Data</div>
        <div className="mt-2 text-[12px] text-[#A1A1AA]">States ranked by demand (scouts + businesses) vs. current operator coverage.</div>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-[12.5px]" data-testid="admin-operator-demand">
            <thead className="text-[#52525b] uppercase tracking-widest text-[10px]">
              <tr>
                <th className="text-left py-2 pr-4">State</th>
                <th className="text-right py-2 px-2">Scouts</th>
                <th className="text-right py-2 px-2">Businesses</th>
                <th className="text-right py-2 px-2">Operators</th>
                <th className="text-right py-2 pl-2">Demand Score</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#2A2F34]">
              {demand.slice(0, 10).map((row) => (
                <tr key={row.state}>
                  <td className="py-2 pr-4 font-bold">{row.state}</td>
                  <td className="py-2 px-2 text-right">{row.scouts}</td>
                  <td className="py-2 px-2 text-right">{row.businesses}</td>
                  <td className="py-2 px-2 text-right">{row.operators}</td>
                  <td className="py-2 pl-2 text-right text-[#FFC107] font-black">{row.demand_score}</td>
                </tr>
              ))}
              {demand.length === 0 && (
                <tr><td colSpan={5} className="py-4 text-[#A1A1AA]">No demand signal yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="grid lg:grid-cols-3 gap-6">
        <div className="sb-card p-6">
          <div className="sb-eyebrow">Recent Signups · Real Data</div>
          <div className="mt-4 divide-y divide-[#2A2F34]" data-testid="admin-recent-signups">
            {recent.length === 0 && <div className="py-6 text-[13px] text-[#A1A1AA]">No signups yet.</div>}
            {recent.slice(0, 12).map((r) => (
              <div key={r.id} className="py-3 flex items-center justify-between text-[12.5px] gap-3">
                <div className="min-w-0">
                  <div className="truncate"><span className="sb-eyebrow mr-2" style={{ fontSize: 9 }}>{r.type}</span>{r.display_name}</div>
                  <div className="text-[#52525b] text-[11px]">{r.city_state} · {new Date(r.created_at).toLocaleString()}</div>
                </div>
                {r.is_referred && <span className="sb-pill sb-pill-gold" style={{ fontSize: 10 }}>ref: {r.referral_code_used}</span>}
              </div>
            ))}
          </div>
        </div>
        <div className="sb-card p-6">
          <div className="sb-eyebrow">Top Referrals · Real Data</div>
          <div className="mt-4 divide-y divide-[#2A2F34]" data-testid="admin-top-referrals">
            {topReferrals.length === 0 && <div className="py-6 text-[13px] text-[#A1A1AA]">No referral traffic yet.</div>}
            {topReferrals.map((r) => (
              <div key={r.code} className="py-3 flex items-center justify-between">
                <div>
                  <div className="text-[13px] font-bold sb-mono">{r.code}</div>
                  <div className="text-[11px] text-[#A1A1AA]">{r.owner_type} · {r.clicks} clicks</div>
                </div>
                <div className="text-[13px] font-black text-[#FFC107]">{r.total_signups} signups</div>
              </div>
            ))}
          </div>
        </div>
        <Link to="/admin/launch-readiness" className="sb-card p-6 hover:border-[#FFC107]/50 transition-colors flex flex-col">
          <div className="sb-eyebrow">Launch Readiness</div>
          <div className="mt-3 text-[22px] font-black">Owner View</div>
          <div className="mt-4 text-[12px] text-[#A1A1AA] flex-1">Track Phase 0 readiness across scouts, operators, businesses, and creators (real data).</div>
          <div className="mt-4 text-[12px] text-[#FFC107] flex items-center gap-1">Open dashboard <ArrowRight size={12} /></div>
        </Link>
      </section>
    </>
  );
}

// -------------------------------------------------------------------------
// DELETE-with-confirmation helper (shared)
// -------------------------------------------------------------------------

function DeleteRegistrationButton({ entityType, rowId, onDeleted }) {
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function doDelete() {
    setBusy(true); setErr("");
    try {
      const r = await authedFetch(`/api/admin/registration/${entityType}/${rowId}`, { method: "DELETE" });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setErr(data?.detail || "Delete failed.");
        setBusy(false);
        return;
      }
      toast.success("Registration deleted");
      setConfirm(false); setBusy(false);
      onDeleted?.();
    } catch (e) {
      setErr("Network error.");
      setBusy(false);
    }
  }

  return (
    <>
      <button
        onClick={() => setConfirm(true)}
        data-testid={`admin-delete-${entityType}-${rowId}`}
        className="inline-flex items-center gap-2 text-[12px] font-bold px-3 py-2 rounded-lg border border-[#EF4444]/50 text-[#EF4444] hover:bg-[#EF4444]/10 transition-colors"
      >
        <Trash2 size={13} /> Delete Registration
      </button>
      {confirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4" onClick={() => !busy && setConfirm(false)}>
          <div className="sb-card p-6 w-full max-w-md border-[#EF4444]/40" onClick={(e) => e.stopPropagation()} data-testid="admin-delete-confirm">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#EF4444]/10 text-[#EF4444] flex items-center justify-center shrink-0">
                <AlertTriangle size={18} />
              </div>
              <div className="min-w-0">
                <div className="font-black text-[16px]">Permanently delete this registration?</div>
                <div className="mt-1 text-[13px] text-[#A1A1AA]">Permanently delete this registration? This cannot be undone.</div>
                {err && <div className="mt-3 text-[12px] text-[#EF4444]" data-testid="admin-delete-error">{err}</div>}
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setConfirm(false)} disabled={busy} data-testid="admin-delete-cancel" className="sb-btn-secondary text-[12px]" style={{ padding: "8px 14px" }}>Cancel</button>
              <button onClick={doDelete} disabled={busy} data-testid="admin-delete-confirm-submit"
                className="inline-flex items-center gap-2 text-[12px] font-bold px-4 py-2 rounded-lg bg-[#EF4444] text-white hover:bg-[#DC2626] disabled:opacity-50 transition-colors">
                {busy ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />} Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// -------------------------------------------------------------------------
// REGISTRATIONS SEARCH (+ delete)
// -------------------------------------------------------------------------

const REG_ENTITY = { scouts: "scout", operators: "operator", businesses: "business", creators: "creator" };

function RegistrationsSearch({ initialKind = "scouts", lockKind = false }) {
  const [kind, setKind] = useState(initialKind);
  const [q, setQ] = useState("");
  const [state, setState] = useState("");
  const [referred, setReferred] = useState("any");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState(null);

  const search = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (state) params.set("state", state.toUpperCase());
      if (referred !== "any") params.set("referred", referred);
      const r = await authedFetch(`/api/admin/registrations/${kind}?${params.toString()}`);
      const data = await r.json().catch(() => ({}));
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch { setRows([]); }
    finally { setLoading(false); }
  }, [kind, q, state, referred]);

  useEffect(() => { setKind(initialKind); }, [initialKind]);
  useEffect(() => { search(); }, [kind]); // eslint-disable-line react-hooks/exhaustive-deps

  async function openDetail(id) {
    const r = await authedFetch(`/api/admin/registrations/${kind}/${id}`);
    if (!r.ok) return;
    setSelected(await r.json());
  }

  return (
    <section className="sb-card p-5" data-testid="admin-registrations-section">
      <div className="sb-eyebrow flex items-center gap-2"><Search size={12} className="text-[#FFC107]" /> {lockKind ? `${kind.charAt(0).toUpperCase() + kind.slice(1)}` : "Registrations Search"}</div>
      <div className="mt-4 flex flex-wrap gap-2 items-end">
        {!lockKind && (
          <div>
            <div className="sb-label">Type</div>
            <select className="sb-input" value={kind} onChange={(e) => setKind(e.target.value)} data-testid="admin-search-kind">
              <option value="scouts">Scouts</option>
              <option value="operators">Operators</option>
              <option value="businesses">Businesses</option>
              <option value="creators">Creators</option>
            </select>
          </div>
        )}
        <div className="flex-1 min-w-[200px]">
          <div className="sb-label">Search email / name / city</div>
          <input className="sb-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. alex@…" data-testid="admin-search-q" onKeyDown={(e) => { if (e.key === "Enter") search(); }} />
        </div>
        <div>
          <div className="sb-label">State</div>
          <input className="sb-input uppercase" maxLength={2} value={state} onChange={(e) => setState(e.target.value)} data-testid="admin-search-state" />
        </div>
        <div>
          <div className="sb-label">Referred</div>
          <select className="sb-input" value={referred} onChange={(e) => setReferred(e.target.value)} data-testid="admin-search-referred">
            <option value="any">Any</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </div>
        <button onClick={search} disabled={loading} data-testid="admin-search-submit" className="sb-btn-primary text-[11px]" style={{ padding: "10px 16px" }}>
          {loading ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />} Search
        </button>
      </div>
      <div className="mt-4 text-[11px] text-[#A1A1AA]">{total} match{total === 1 ? "" : "es"} · showing up to 50</div>
      <div className="mt-2 overflow-x-auto">
        <table className="w-full text-[12.5px]" data-testid="admin-search-results">
          <thead className="text-[#52525b] uppercase tracking-widest text-[10px]">
            <tr>
              <th className="text-left py-2 pr-4">Name / Business</th>
              <th className="text-left py-2 pr-4">Email</th>
              <th className="text-left py-2 pr-4">City / State</th>
              <th className="text-left py-2 pr-4">Created</th>
              <th className="text-left py-2 pr-4">Referral</th>
              <th className="text-left py-2 pr-4">Email Sent</th>
              <th className="text-left py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#2A2F34]">
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="py-2 pr-4">{r.display_name}</td>
                <td className="py-2 pr-4 text-[#A1A1AA]">{r.email}</td>
                <td className="py-2 pr-4 text-[#A1A1AA]">{r.city_state || "—"}</td>
                <td className="py-2 pr-4 text-[#A1A1AA]">{new Date(r.created_at).toLocaleDateString()}</td>
                <td className="py-2 pr-4">{r.referral_code_used ? <span className="sb-pill sb-pill-gold" style={{ fontSize: 10 }}>{r.referral_code_used}</span> : <span className="text-[#52525b]">—</span>}</td>
                <td className="py-2 pr-4">{r.email_sent ? <span className="text-[#22C55E]">Yes</span> : <span className="text-[#F59E0B]">No</span>}</td>
                <td className="py-2"><button onClick={() => openDetail(r.id)} data-testid={`admin-row-open-${r.id}`} className="text-[#FFC107] text-[11px] font-bold">Open</button></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="py-4 text-[#A1A1AA]">No matches.</td></tr>}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setSelected(null)}>
          <div className="sb-card p-6 w-full max-w-2xl max-h-[85vh] overflow-y-auto sb-scroll" onClick={(e) => e.stopPropagation()} data-testid="admin-detail-modal">
            <div className="flex items-center justify-between">
              <div>
                <div className="sb-eyebrow">Registration Detail</div>
                <div className="mt-1 text-[20px] font-black">{selected.display_name}</div>
              </div>
              <button onClick={() => setSelected(null)} className="text-[#A1A1AA] hover:text-white text-[13px]">Close ✕</button>
            </div>
            <div className="mt-4 divide-y divide-[#2A2F34]">
              {Object.entries(selected).filter(([k]) => k !== "display_name").map(([k, v]) => (
                <div key={k} className="py-2 flex items-start gap-3 text-[12.5px]">
                  <div className="sb-eyebrow w-40 shrink-0" style={{ fontSize: 10 }}>{k}</div>
                  <div className="flex-1 text-white/90 break-all">{v === null || v === "" ? <span className="text-[#52525b]">—</span> : Array.isArray(v) ? v.join(", ") : String(v)}</div>
                </div>
              ))}
            </div>
            <div className="mt-6 flex justify-end gap-2">
              {kind === "operators" && <OperatorStatusActions op={selected} onChange={(fresh) => { setSelected(fresh); search(); }} />}
              <DeleteRegistrationButton
                entityType={REG_ENTITY[kind]}
                rowId={selected.id}
                onDeleted={() => { setSelected(null); search(); }}
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function OperatorStatusActions({ op, onChange }) {
  const [busy, setBusy] = useState(null);
  const status = op.status || "pending";
  async function call(action) {
    setBusy(action);
    const r = await authedFetch(`/api/admin/operators/${op.id}/${action}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
    setBusy(null);
    if (!r.ok) { const d = await r.json().catch(() => ({})); toast.error(d?.detail || "Failed"); return; }
    toast.success(`Operator ${action}`);
    // Refresh detail
    const rr = await authedFetch(`/api/admin/registrations/operators/${op.id}`);
    if (rr.ok) onChange(await rr.json());
  }
  const TONE = { pending: "amber", approved: "green", suspended: "red", rejected: "red" };
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="admin-op-status-actions">
      <span className={`sb-pill sb-pill-${TONE[status] || "gray"}`} data-testid="admin-op-status-pill">{formatEnum(status)}</span>
      {status !== "approved" && (
        <button onClick={() => call("approve")} disabled={busy} className="text-[11px] px-3 py-1.5 rounded bg-[#22C55E] text-[#080B0D] font-bold" data-testid="admin-op-approve">
          {busy === "approve" ? <Loader2 size={11} className="animate-spin" /> : "Approve"}
        </button>
      )}
      {status === "pending" && (
        <button onClick={() => call("reject")} disabled={busy} className="text-[11px] px-3 py-1.5 rounded border border-[#EF4444] text-[#EF4444]" data-testid="admin-op-reject">
          {busy === "reject" ? <Loader2 size={11} className="animate-spin" /> : "Reject"}
        </button>
      )}
      {status === "approved" && (
        <button onClick={() => call("suspend")} disabled={busy} className="text-[11px] px-3 py-1.5 rounded border border-[#F59E0B] text-[#F59E0B]" data-testid="admin-op-suspend">
          {busy === "suspend" ? <Loader2 size={11} className="animate-spin" /> : "Suspend"}
        </button>
      )}
      {(status === "suspended" || status === "rejected") && (
        <button onClick={() => call("reinstate")} disabled={busy} className="text-[11px] px-3 py-1.5 rounded border border-[#22C55E] text-[#22C55E]" data-testid="admin-op-reinstate">
          {busy === "reinstate" ? <Loader2 size={11} className="animate-spin" /> : "Reinstate"}
        </button>
      )}
    </div>
  );
}


// -------------------------------------------------------------------------
// SELLER INTERESTS (+ delete)
// -------------------------------------------------------------------------

const SELLER_TYPE_LABEL = {
  existing_location: "Existing Location",
  turnkey: "Turnkey Location + Machine",
  route: "Vending Route",
};

function SellerInterestsSection() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [sellerType, setSellerType] = useState("");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (q) p.set("q", q);
      if (sellerType) p.set("seller_type", sellerType);
      const r = await authedFetch(`/api/admin/seller_interests?${p.toString()}`);
      const j = await r.json().catch(() => ({}));
      setRows(j.rows || []);
      setTotal(j.total || 0);
    } catch { setRows([]); }
    finally { setLoading(false); }
  }, [q, sellerType]);

  useEffect(() => { load(); }, [sellerType]); // eslint-disable-line react-hooks/exhaustive-deps

  async function openDetail(id) {
    const r = await authedFetch(`/api/admin/seller_interests/${id}`);
    if (!r.ok) return;
    setSelected(await r.json());
  }

  async function exportCsv() {
    setBusy(true);
    try {
      const r = await authedFetch("/api/admin/export/seller_interests.csv");
      if (!r.ok) throw new Error("export failed");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "spotbounty-seller_interests.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("CSV export failed"); }
    finally { setBusy(false); }
  }

  const fmtMoney = (v) => (v === null || v === undefined || v === "" ? "" : `$${Number(v).toLocaleString()}`);
  const grossFor = (r) => (r.approx_monthly_gross_sales ?? null);

  const detailGroups = selected
    ? [
        { label: "Seller Info", keys: ["first_name","last_name","email","phone","state","created_at"] },
        { label: "Asset Info", keys: ["seller_type","asset_city","asset_state","asset_zip"] },
        { label: "Location / Turnkey", keys: ["location_type","vending_operating_duration","machine_currently_installed","approx_monthly_gross_sales","commission_rev_share","manager_aware_of_sale","machine_type","machine_brand_model","machine_age_years","cashless_reader","asking_price"] },
        { label: "Route", keys: ["route_num_locations","route_num_machines","route_cities_states","route_total_monthly_gross"] },
        { label: "General", keys: ["reason_for_selling","additional_notes","turnstile_verified","email_sent","email_last_attempt_at"] },
      ]
    : [];

  return (
    <section className="sb-card p-5" data-testid="admin-seller-interests-section">
      <div className="sb-eyebrow flex items-center gap-2"><Store size={12} className="text-[#FFC107]" /> Seller Interests</div>
      <div className="mt-4 flex flex-wrap gap-2 items-end">
        <div>
          <div className="sb-label">Type</div>
          <select className="sb-input" value={sellerType} onChange={(e) => setSellerType(e.target.value)} data-testid="admin-seller-type-filter">
            <option value="">All</option>
            <option value="existing_location">Existing Location</option>
            <option value="turnkey">Turnkey Location + Machine</option>
            <option value="route">Vending Route</option>
          </select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <div className="sb-label">Search email / name / city</div>
          <input className="sb-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") load(); }} placeholder="e.g. alex@…" data-testid="admin-seller-q" />
        </div>
        <button onClick={load} className="sb-btn-secondary" data-testid="admin-seller-search">Search</button>
        <button onClick={exportCsv} disabled={busy} className="sb-btn-secondary" data-testid="admin-seller-export-csv">{busy ? "Exporting…" : "Export CSV"}</button>
      </div>

      <div className="mt-3 text-[12px] text-[#A1A1AA]" data-testid="admin-seller-count">Total: {total}</div>

      <div className="mt-3 overflow-auto">
        <table className="w-full text-[12.5px] min-w-[900px]" data-testid="admin-seller-table">
          <thead className="text-[#A1A1AA]">
            <tr className="text-left">
              <th className="py-2 px-2">Seller</th>
              <th className="py-2 px-2">Email</th>
              <th className="py-2 px-2">Phone</th>
              <th className="py-2 px-2">Type</th>
              <th className="py-2 px-2">City / State</th>
              <th className="py-2 px-2 text-right">Monthly gross</th>
              <th className="py-2 px-2 text-right">Asking</th>
              <th className="py-2 px-2">Created</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#2A2F34]">
            {loading && (<tr><td colSpan={8} className="py-6 text-center text-[#A1A1AA]">Loading…</td></tr>)}
            {!loading && rows.length === 0 && (<tr><td colSpan={8} className="py-6 text-center text-[#52525b]">No seller interests yet.</td></tr>)}
            {!loading && rows.map((r) => (
              <tr key={r.id} onClick={() => openDetail(r.id)} className="cursor-pointer hover:bg-white/[.03]" data-testid={`admin-seller-row-${r.id}`}>
                <td className="py-2 px-2">{r.first_name} {r.last_name}</td>
                <td className="py-2 px-2 break-all">{r.email}</td>
                <td className="py-2 px-2">{r.phone}</td>
                <td className="py-2 px-2">{SELLER_TYPE_LABEL[r.seller_type] || r.seller_type}</td>
                <td className="py-2 px-2">{r.asset_city}{r.asset_state ? `, ${r.asset_state}` : ""}</td>
                <td className="py-2 px-2 text-right">{fmtMoney(grossFor(r))}</td>
                <td className="py-2 px-2 text-right">{fmtMoney(r.asking_price)}</td>
                <td className="py-2 px-2 whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setSelected(null)} data-testid="admin-seller-modal">
          <div className="sb-card p-6 w-full max-w-2xl max-h-[85vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div>
                <div className="sb-eyebrow">Seller Interest</div>
                <div className="font-black text-[18px] mt-1">{selected.first_name} {selected.last_name}</div>
                <div className="text-[12px] text-[#A1A1AA]">{SELLER_TYPE_LABEL[selected.seller_type] || selected.seller_type}</div>
              </div>
              <button onClick={() => setSelected(null)} className="text-[#A1A1AA] hover:text-white text-[13px]" data-testid="admin-seller-modal-close">Close ✕</button>
            </div>
            <div className="mt-4 space-y-4">
              {detailGroups.map((g) => (
                <div key={g.label}>
                  <div className="sb-eyebrow" style={{ fontSize: 10 }}>{g.label}</div>
                  <div className="mt-1 divide-y divide-[#2A2F34]">
                    {g.keys.filter((k) => k in selected).map((k) => {
                      const v = selected[k];
                      const shown = v === null || v === "" || v === undefined
                        ? <span className="text-[#52525b]">—</span>
                        : (typeof v === "boolean" ? String(v) : String(v));
                      return (
                        <div key={k} className="py-2 flex items-start gap-3 text-[12.5px]">
                          <div className="w-52 shrink-0 text-[#A1A1AA]">{k}</div>
                          <div className="flex-1 text-white/90 break-all">{shown}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 flex justify-end">
              <DeleteRegistrationButton
                entityType="seller_interest"
                rowId={selected.id}
                onDeleted={() => { setSelected(null); load(); }}
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

// -------------------------------------------------------------------------
// CONTACT MESSAGES (read-only)
// -------------------------------------------------------------------------

function ContactMessagesSection() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (q) p.set("q", q);
      const r = await authedFetch(`/api/admin/contact_messages?${p.toString()}`);
      const j = await r.json().catch(() => ({}));
      setRows(j.rows || []);
      setTotal(j.total || 0);
    } catch { setRows([]); }
    finally { setLoading(false); }
  }, [q]);

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function openDetail(id) {
    const r = await authedFetch(`/api/admin/contact_messages/${id}`);
    if (!r.ok) return;
    setSelected(await r.json());
  }

  return (
    <section className="sb-card p-5" data-testid="admin-contact-section">
      <div className="sb-eyebrow flex items-center gap-2"><MessageSquare size={12} className="text-[#FFC107]" /> Contact Messages</div>

      <div className="mt-4 flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[200px]">
          <div className="sb-label">Search email / name / subject</div>
          <input className="sb-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") load(); }} placeholder="e.g. jane@…" data-testid="admin-contact-q" />
        </div>
        <button onClick={load} className="sb-btn-secondary" data-testid="admin-contact-search">Search</button>
      </div>

      <div className="mt-3 text-[12px] text-[#A1A1AA]" data-testid="admin-contact-count">Total: {total}</div>

      <div className="mt-3 overflow-auto">
        <table className="w-full text-[12.5px] min-w-[720px]" data-testid="admin-contact-table">
          <thead className="text-[#A1A1AA]">
            <tr className="text-left">
              <th className="py-2 px-2">Name</th>
              <th className="py-2 px-2">Email</th>
              <th className="py-2 px-2">Subject / Preview</th>
              <th className="py-2 px-2">Created</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#2A2F34]">
            {loading && (<tr><td colSpan={4} className="py-6 text-center text-[#A1A1AA]">Loading…</td></tr>)}
            {!loading && rows.length === 0 && (<tr><td colSpan={4} className="py-6 text-center text-[#52525b]">No messages yet.</td></tr>)}
            {!loading && rows.map((r) => (
              <tr key={r.id} onClick={() => openDetail(r.id)} className="cursor-pointer hover:bg-white/[.03]" data-testid={`admin-contact-row-${r.id}`}>
                <td className="py-2 px-2">{r.name}</td>
                <td className="py-2 px-2 break-all">{r.email}</td>
                <td className="py-2 px-2 truncate max-w-[380px]">{r.subject || r.preview || "—"}</td>
                <td className="py-2 px-2 whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setSelected(null)} data-testid="admin-contact-modal">
          <div className="sb-card p-6 w-full max-w-2xl max-h-[85vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div>
                <div className="sb-eyebrow">Contact Message</div>
                <div className="font-black text-[18px] mt-1">{selected.name}</div>
                <div className="text-[12px] text-[#A1A1AA] break-all">{selected.email}</div>
              </div>
              <button onClick={() => setSelected(null)} className="text-[#A1A1AA] hover:text-white text-[13px]" data-testid="admin-contact-modal-close">Close ✕</button>
            </div>
            <div className="mt-4 divide-y divide-[#2A2F34]">
              {["subject","message","user_agent","ip_hash","created_at"].map((k) => (
                <div key={k} className="py-2 flex items-start gap-3 text-[12.5px]">
                  <div className="sb-eyebrow w-40 shrink-0" style={{ fontSize: 10 }}>{k}</div>
                  <div className="flex-1 text-white/90 break-all whitespace-pre-wrap">{selected[k] === null || selected[k] === "" ? <span className="text-[#52525b]">—</span> : String(selected[k])}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

// =========================================================================
// OUTREACH DESK
// =========================================================================

const STATUS_LABEL = {
  new: "New", draft_generated: "Draft", ready_for_review: "Ready",
  sent: "Sent", follow_up: "Follow-up", interested: "Interested",
  registered: "Registered", not_interested: "Not Interested",
  do_not_contact: "Do Not Contact",
};
const STATUS_TONE = {
  new: "text-[#A1A1AA] border-[#2A2F34]",
  draft_generated: "text-[#F59E0B] border-[#F59E0B]/40",
  ready_for_review: "text-[#F59E0B] border-[#F59E0B]/40",
  sent: "text-[#3B82F6] border-[#3B82F6]/40",
  follow_up: "text-[#F59E0B] border-[#F59E0B]/40",
  interested: "text-[#22C55E] border-[#22C55E]/40",
  registered: "text-[#FFC107] border-[#FFC107]/40",
  not_interested: "text-[#52525b] border-[#2A2F34]",
  do_not_contact: "text-[#EF4444] border-[#EF4444]/40",
};

function Badge({ status }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10.5px] font-bold uppercase tracking-wider ${STATUS_TONE[status] || STATUS_TONE.new}`}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function OutreachSection() {
  const [stats, setStats] = useState(null);
  const [gmail, setGmail] = useState({ connected: false });
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [typeF, setTypeF] = useState("");
  const [statusF, setStatusF] = useState("");
  const [loading, setLoading] = useState(false);
  const [followUps, setFollowUps] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (q) p.set("q", q);
      if (typeF) p.set("type", typeF);
      if (statusF) p.set("status", statusF);
      const [lst, st, gm, fu] = await Promise.all([
        authedFetch(`/api/admin/outreach/prospects?${p.toString()}`).then((r) => r.ok ? r.json() : { rows: [], total: 0 }),
        authedFetch("/api/admin/outreach/stats").then((r) => r.ok ? r.json() : null),
        authedFetch("/api/admin/outreach/oauth/status").then((r) => r.ok ? r.json() : { connected: false }),
        authedFetch("/api/admin/outreach/follow_ups").then((r) => r.ok ? r.json() : { rows: [] }),
      ]);
      setRows(lst.rows || []); setTotal(lst.total || 0);
      setStats(st); setGmail(gm || { connected: false });
      setFollowUps(fu.rows || []);
    } finally { setLoading(false); }
  }, [q, typeF, statusF]);

  useEffect(() => { load(); }, [typeF, statusF]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    // gmail_connected query param toast
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("gmail_connected") === "1") { toast.success("Gmail connected"); window.history.replaceState({}, "", "/admin"); }
    if (sp.get("gmail_connected") === "0") { toast.error("Gmail connect failed"); window.history.replaceState({}, "", "/admin"); }
  }, []);

  async function connectGmail() {
    const r = await authedFetch("/api/admin/outreach/oauth/start");
    if (r.status === 503) {
      const d = await r.json().catch(() => ({}));
      toast.error(`Gmail OAuth not configured. Missing: ${(d.missing_env || []).join(", ") || "env vars"}`);
      return;
    }
    if (!r.ok) { toast.error("Could not start OAuth"); return; }
    const d = await r.json();
    if (d.url) window.location.href = d.url;
  }
  async function disconnectGmail() {
    const r = await authedFetch("/api/admin/outreach/oauth/disconnect", { method: "POST" });
    if (r.ok) { toast.success("Gmail disconnected"); load(); }
  }

  const statCards = stats ? [
    { label: "Prospects", value: stats.prospects },
    { label: "Emails Ready", value: stats.emails_ready, tone: "amber" },
    { label: "Sent Today", value: `${stats.sent_today} / ${stats.daily_limit}` },
    { label: "Follow Ups", value: stats.follow_ups, tone: stats.follow_ups > 0 ? "amber" : "" },
    { label: "Interested", value: stats.interested },
    { label: "Registered", value: stats.registered },
  ] : [];

  return (
    <section data-testid="admin-outreach-section" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="sb-eyebrow">Owner-Only · Outreach</div>
          <h1 className="mt-1 sb-headline text-[36px] md:text-[44px]">OUTREACH <span className="text-[#FFC107]">DESK.</span></h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} data-testid="outreach-refresh" className="sb-btn-secondary text-[11px]" style={{ padding: "10px 14px" }}><RefreshCw size={13} /> Refresh</button>
          {stats?.emails_ready > 0 && (
            <button onClick={() => setReviewOpen(true)} data-testid="outreach-emails-ready" className="sb-btn-secondary text-[11px] border-[#FFC107]/40 text-[#FFC107]" style={{ padding: "10px 14px" }}>
              <MailIcon size={13} /> Emails Ready ({stats.emails_ready})
            </button>
          )}
          <button onClick={() => setBulkOpen(true)} data-testid="outreach-bulk-add" className="sb-btn-secondary text-[11px]" style={{ padding: "10px 14px" }}><Upload size={13} /> Bulk Add</button>
          <button onClick={() => setAddOpen(true)} data-testid="outreach-add-prospect" className="sb-btn-primary text-[11px]" style={{ padding: "10px 14px" }}><Plus size={13} /> Add Prospect</button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <section className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="outreach-stats">
          {statCards.map((s) => (
            <div key={s.label} className="sb-card p-4" data-testid={`outreach-stat-${s.label.toLowerCase().replace(/ /g,'-')}`}>
              <div className="sb-eyebrow" style={{ fontSize: 9 }}>{s.label}</div>
              <div className={`mt-2 text-[18px] font-black ${s.tone === "amber" ? "text-[#F59E0B]" : "text-white"}`}>{s.value}</div>
            </div>
          ))}
        </section>
      )}

      {/* Gmail connection */}
      <section className="sb-card p-5 flex items-center justify-between flex-wrap gap-3" data-testid="outreach-gmail-status">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${gmail.connected ? "bg-[#22C55E]/10 text-[#22C55E]" : "bg-[#2A2F34] text-[#A1A1AA]"}`}>
            <MailIcon size={18} />
          </div>
          <div>
            <div className="sb-eyebrow" style={{ fontSize: 10 }}>Gmail</div>
            <div className="mt-0.5 text-[13.5px] font-bold">
              {gmail.connected
                ? <>Connected as <span className="sb-mono text-[#22C55E]">{gmail.email || "gmail user"}</span></>
                : "Not connected"}
            </div>
          </div>
        </div>
        <div>
          {gmail.connected
            ? <button onClick={disconnectGmail} data-testid="outreach-gmail-disconnect" className="sb-btn-secondary text-[11px]" style={{ padding: "10px 14px" }}><ShieldOff size={13} /> Disconnect</button>
            : <button onClick={connectGmail} data-testid="outreach-gmail-connect" className="sb-btn-primary text-[11px]" style={{ padding: "10px 14px" }}><Link2 size={13} /> Connect Gmail</button>}
        </div>
      </section>

      {/* Follow-ups panel */}
      {followUps.length > 0 && (
        <section className="sb-card p-5 border-[#F59E0B]/40" data-testid="outreach-followups">
          <div className="sb-eyebrow flex items-center gap-2"><Clock size={12} className="text-[#F59E0B]" /> Follow-ups Due</div>
          <div className="mt-3 divide-y divide-[#2A2F34]">
            {followUps.slice(0, 12).map((f) => (
              <div key={f.id} onClick={() => setSelectedId(f.id)} className="py-2 flex items-center justify-between cursor-pointer hover:bg-white/[.03] px-2 -mx-2 rounded" data-testid={`outreach-followup-${f.id}`}>
                <div className="min-w-0">
                  <div className="text-[13px] font-bold truncate">{f.company_name}</div>
                  <div className="text-[11px] text-[#A1A1AA]">{f.contact_name || f.email} · due {new Date(f.next_follow_up_at).toLocaleDateString()}</div>
                </div>
                <Badge status={f.status} />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Filters + list */}
      <section className="sb-card p-5">
        <div className="sb-eyebrow flex items-center gap-2"><Send size={12} className="text-[#FFC107]" /> Prospects ({total})</div>
        <div className="mt-4 flex flex-wrap gap-2 items-end">
          <div>
            <div className="sb-label">Type</div>
            <select className="sb-input" value={typeF} onChange={(e) => setTypeF(e.target.value)} data-testid="outreach-type-filter">
              <option value="">All</option><option value="business">Business</option><option value="operator">Operator</option>
            </select>
          </div>
          <div>
            <div className="sb-label">Status</div>
            <select className="sb-input" value={statusF} onChange={(e) => setStatusF(e.target.value)} data-testid="outreach-status-filter">
              <option value="">All</option>
              {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <div className="sb-label">Search company / contact / email / city</div>
            <input className="sb-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") load(); }} placeholder="e.g. Meridian" data-testid="outreach-q" />
          </div>
          <button onClick={load} className="sb-btn-secondary" data-testid="outreach-search">Search</button>
        </div>

        <div className="mt-4 overflow-auto">
          <table className="w-full text-[12.5px] min-w-[800px]" data-testid="outreach-table">
            <thead className="text-[#A1A1AA]">
              <tr className="text-left">
                <th className="py-2 px-2">Company / Contact</th>
                <th className="py-2 px-2">Email</th>
                <th className="py-2 px-2">Type</th>
                <th className="py-2 px-2">Status</th>
                <th className="py-2 px-2">Last Contact</th>
                <th className="py-2 px-2">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#2A2F34]">
              {loading && (<tr><td colSpan={6} className="py-6 text-center text-[#A1A1AA]">Loading…</td></tr>)}
              {!loading && rows.length === 0 && (<tr><td colSpan={6} className="py-6 text-center text-[#52525b]">No prospects yet. Click "Add Prospect" to start.</td></tr>)}
              {!loading && rows.map((r) => (
                <tr key={r.id} onClick={() => setSelectedId(r.id)} className="cursor-pointer hover:bg-white/[.03]" data-testid={`outreach-row-${r.id}`}>
                  <td className="py-2 px-2">
                    <div className="font-bold">{r.company_name}</div>
                    {r.contact_name && <div className="text-[11px] text-[#A1A1AA]">{r.contact_name}</div>}
                  </td>
                  <td className="py-2 px-2 break-all text-[#A1A1AA]">{r.email}</td>
                  <td className="py-2 px-2 uppercase text-[10px] tracking-widest text-[#A1A1AA]">{r.prospect_type}</td>
                  <td className="py-2 px-2"><Badge status={r.status} /></td>
                  <td className="py-2 px-2 text-[#A1A1AA] whitespace-nowrap">{r.last_contact_at ? new Date(r.last_contact_at).toLocaleDateString() : "—"}</td>
                  <td className="py-2 px-2 text-[#A1A1AA] whitespace-nowrap">{new Date(r.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {addOpen && <AddProspectModal onClose={() => setAddOpen(false)} onCreated={(id) => { setAddOpen(false); load(); setSelectedId(id); }} />}
      {bulkOpen && <BulkAddProspectsModal onClose={() => setBulkOpen(false)} onComplete={() => { setBulkOpen(false); load(); }} />}
      {reviewOpen && <BatchReviewModal onClose={() => setReviewOpen(false)} onComplete={() => { setReviewOpen(false); load(); }} />}
      {selectedId && <ProspectDetailModal prospectId={selectedId} gmailConnected={gmail.connected} onClose={() => { setSelectedId(null); load(); }} />}
    </section>
  );
}

function AddProspectModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    prospect_type: "business", company_name: "", contact_name: "", email: "",
    phone: "", website: "", city: "", state: "", zip: "",
    industry_location_type: "", source: "", source_url: "", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [dup, setDup] = useState(null);

  function up(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setErr(""); setDup(null);
    try {
      const r = await authedFetch("/api/admin/outreach/prospects", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await r.json().catch(() => ({}));
      if (r.status === 409 && data.suppressed) { setErr("This email is on the suppression list."); setBusy(false); return; }
      if (r.status === 409 && data.duplicate) { setDup(data.existing_id); setBusy(false); return; }
      if (!r.ok) { setErr(data.detail || "Could not create prospect."); setBusy(false); return; }
      onCreated?.(data.id);
    } catch { setErr("Network error."); setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => !busy && onClose()} data-testid="outreach-add-modal">
      <form onSubmit={submit} className="sb-card p-6 w-full max-w-2xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <div className="sb-eyebrow">Add Prospect</div>
            <div className="mt-1 text-[20px] font-black">New outreach target</div>
          </div>
          <button type="button" onClick={onClose} className="text-[#A1A1AA] hover:text-white" data-testid="outreach-add-close"><X size={18} /></button>
        </div>

        <div className="mt-5 flex gap-2">
          {[{ v: "business", l: "Business" }, { v: "operator", l: "Operator" }].map((t) => (
            <button type="button" key={t.v} onClick={() => up("prospect_type", t.v)}
              data-testid={`outreach-add-type-${t.v}`}
              className={`flex-1 py-2 rounded-lg border text-[13px] font-bold transition-colors ${form.prospect_type === t.v ? "border-[#FFC107] text-[#FFC107] bg-[#FFC107]/10" : "border-[#2A2F34] text-[#A1A1AA] hover:text-white"}`}>
              {t.l}
            </button>
          ))}
        </div>

        <div className="mt-5 grid sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <div className="sb-label">{form.prospect_type === "operator" ? "Operator / Company name *" : "Business name *"}</div>
            <input required className="sb-input" value={form.company_name} onChange={(e) => up("company_name", e.target.value)} data-testid="outreach-add-company" />
          </div>
          <div><div className="sb-label">Contact name</div><input className="sb-input" value={form.contact_name} onChange={(e) => up("contact_name", e.target.value)} data-testid="outreach-add-contact" /></div>
          <div><div className="sb-label">Email *</div><input required type="email" className="sb-input" value={form.email} onChange={(e) => up("email", e.target.value)} data-testid="outreach-add-email" /></div>
          <div><div className="sb-label">Phone</div><input className="sb-input" value={form.phone} onChange={(e) => up("phone", e.target.value)} /></div>
          <div><div className="sb-label">Website</div><input className="sb-input" value={form.website} onChange={(e) => up("website", e.target.value)} /></div>
          <div><div className="sb-label">City</div><input className="sb-input" value={form.city} onChange={(e) => up("city", e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><div className="sb-label">State</div><input className="sb-input uppercase" maxLength={2} value={form.state} onChange={(e) => up("state", e.target.value)} /></div>
            <div><div className="sb-label">ZIP</div><input className="sb-input" value={form.zip} onChange={(e) => up("zip", e.target.value)} /></div>
          </div>
          <div className="sm:col-span-2">
            <div className="sb-label">{form.prospect_type === "operator" ? "Machine types / focus" : "Industry / location type"}</div>
            <input className="sb-input" value={form.industry_location_type} onChange={(e) => up("industry_location_type", e.target.value)} />
          </div>
          <div><div className="sb-label">Source</div><input className="sb-input" value={form.source} onChange={(e) => up("source", e.target.value)} placeholder="e.g. Google Maps" /></div>
          <div><div className="sb-label">Source URL</div><input className="sb-input" value={form.source_url} onChange={(e) => up("source_url", e.target.value)} /></div>
          <div className="sm:col-span-2">
            <div className="sb-label">Notes</div>
            <textarea rows={3} className="sb-input" value={form.notes} onChange={(e) => up("notes", e.target.value)} data-testid="outreach-add-notes" />
          </div>
        </div>

        {err && <div className="mt-4 text-[12px] text-[#EF4444]" data-testid="outreach-add-error">{err}</div>}
        {dup && (
          <div className="mt-4 text-[12px] text-[#F59E0B]" data-testid="outreach-add-duplicate">
            This prospect already exists.{" "}
            <button type="button" onClick={() => onCreated?.(dup)} className="underline hover:text-white">Open existing</button>
          </div>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={busy} className="sb-btn-secondary text-[12px]" style={{ padding: "8px 14px" }}>Cancel</button>
          <button type="submit" disabled={busy} data-testid="outreach-add-submit" className="sb-btn-primary text-[12px]" style={{ padding: "8px 16px" }}>
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Create
          </button>
        </div>
      </form>
    </div>
  );
}

// ─── BULK ADD PROSPECTS ────────────────────────────────────────────────
const BULK_COLUMNS = [
  { key: "business_name", labels: ["business name","business","company","company name"], display: "Business Name" },
  { key: "contact_name",  labels: ["contact name","contact"], display: "Contact Name" },
  { key: "email",         labels: ["email"], display: "Email" },
  { key: "phone",         labels: ["phone"], display: "Phone" },
  { key: "website",       labels: ["website"], display: "Website" },
  { key: "city",          labels: ["city"], display: "City" },
  { key: "state",         labels: ["state"], display: "State" },
  { key: "zip",           labels: ["zip"], display: "ZIP" },
  { key: "industry_location_type", labels: ["industry / location type","industry","location type"], display: "Industry" },
  { key: "source",        labels: ["source"], display: "Source" },
  { key: "source_url",    labels: ["source url","url"], display: "Source URL" },
  { key: "notes",         labels: ["notes"], display: "Notes" },
];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function parseBulkPaste(text) {
  const lines = String(text || "").split(/\r?\n/).map((l) => l).filter((l) => l.trim().length > 0);
  if (lines.length === 0) return { rows: [], mapping: [], hadHeader: false };
  const isTSV = lines.some((l) => l.includes("\t"));
  const splitLine = (l) => isTSV ? l.split("\t") : splitCSVRow(l);
  const rawRows = lines.map(splitLine).map((r) => r.map((c) => (c || "").trim()));
  const first = rawRows[0].map((c) => c.toLowerCase().trim());
  let mapping;
  let hadHeader = false;
  const looksLikeHeader = first.some((cell) =>
    BULK_COLUMNS.some((col) => col.labels.includes(cell)),
  );
  if (looksLikeHeader) {
    hadHeader = true;
    mapping = first.map((cell) => {
      const col = BULK_COLUMNS.find((c) => c.labels.includes(cell));
      return col ? col.key : null;
    });
  } else {
    mapping = BULK_COLUMNS.map((c) => c.key);
  }
  const dataRows = (hadHeader ? rawRows.slice(1) : rawRows).map((cells) => {
    const obj = {};
    for (let i = 0; i < mapping.length; i++) {
      const k = mapping[i];
      if (!k) continue;
      obj[k] = cells[i] || "";
    }
    return obj;
  });
  return { rows: dataRows, mapping, hadHeader };
}
function splitCSVRow(line) {
  const out = []; let cur = ""; let q = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (q) {
      if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
      else if (ch === '"') { q = false; }
      else cur += ch;
    } else {
      if (ch === ',') { out.push(cur); cur = ""; }
      else if (ch === '"') q = true;
      else cur += ch;
    }
  }
  out.push(cur);
  return out;
}
function classifyBulkRow(row, seenEmails) {
  const email = String(row.email || "").trim().toLowerCase();
  const company = String(row.business_name || "").trim();
  if (!company || !email) return { status: "missing", label: "Missing required" };
  if (!EMAIL_RE.test(email)) return { status: "invalid", label: "Invalid email" };
  if (seenEmails.has(email)) return { status: "dup_payload", label: "Duplicate (payload)" };
  seenEmails.add(email);
  return { status: "ready", label: "Ready" };
}

function BulkAddProspectsModal({ onClose, onComplete }) {
  const [step, setStep] = useState("input"); // input | preview | result
  const [text, setText] = useState("");
  const [rows, setRows] = useState([]); // parsed
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { results, summary }
  const [prospectType, setProspectType] = useState("business"); // "business" | "operator"
  const [autoDraft, setAutoDraft] = useState(false);

  function doPreview() {
    const parsed = parseBulkPaste(text);
    setRows(parsed.rows);
    setStep("preview");
  }
  function removeRow(i) { setRows((rs) => rs.filter((_, j) => j !== i)); }

  const seenEmails = new Set();
  const classified = rows.map((r) => classifyBulkRow(r, seenEmails));
  const readyCount = classified.filter((c) => c.status === "ready").length;

  async function doCreate() {
    setBusy(true);
    try {
      const payload = rows.map((r) => ({
        business_name: r.business_name || "", contact_name: r.contact_name || "",
        email: r.email || "", phone: r.phone || "", website: r.website || "",
        city: r.city || "", state: r.state || "", zip: r.zip || "",
        industry_location_type: r.industry_location_type || "",
        source: r.source || "", source_url: r.source_url || "", notes: r.notes || "",
      }));
      const r = await authedFetch("/api/admin/outreach/prospects/bulk", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prospects: payload,
          prospect_type: prospectType,
          auto_generate_drafts: autoDraft,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(data.detail || "Bulk import failed");
        setBusy(false);
        return;
      }
      setResult(data);
      setStep("result");
      setBusy(false);
      const created = data.summary?.created || 0;
      if (created > 0) toast.success(`${created} prospect${created === 1 ? "" : "s"} created`);
    } catch {
      toast.error("Network error");
      setBusy(false);
    }
  }

  function close() {
    if (busy) return;
    if (step === "result" && (result?.summary?.created || 0) > 0) onComplete?.();
    else onClose?.();
  }

  const pillFor = (status) => {
    if (status === "ready") return "bg-[#22C55E]/15 text-[#22C55E] border-[#22C55E]/30";
    if (status === "missing") return "bg-[#F59E0B]/15 text-[#F59E0B] border-[#F59E0B]/30";
    if (status === "invalid") return "bg-[#EF4444]/15 text-[#EF4444] border-[#EF4444]/30";
    return "bg-[#2A2F34] text-[#A1A1AA] border-[#2A2F34]";
  };
  const resPillFor = (status) => {
    if (status === "created") return "bg-[#22C55E]/15 text-[#22C55E] border-[#22C55E]/30";
    if (status === "duplicate") return "bg-[#2A2F34] text-[#A1A1AA] border-[#2A2F34]";
    if (status === "invalid") return "bg-[#F59E0B]/15 text-[#F59E0B] border-[#F59E0B]/30";
    return "bg-[#EF4444]/15 text-[#EF4444] border-[#EF4444]/30";
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={close} data-testid="outreach-bulk-modal">
      <div className="sb-card p-6 w-full max-w-4xl max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <div className="sb-eyebrow">Bulk import</div>
            <div className="mt-1 text-[20px] font-black">Bulk Add Prospects</div>
          </div>
          <button type="button" onClick={close} className="text-[#A1A1AA] hover:text-white" data-testid="outreach-bulk-close"><X size={18} /></button>
        </div>

        {step === "input" && (
          <div className="mt-5" data-testid="outreach-bulk-input">
            {/* Prospect-type toggle (batch-level) */}
            <div className="flex gap-2 mb-4">
              {[{ v: "business", l: "Business" }, { v: "operator", l: "Operator" }].map((t) => (
                <button type="button" key={t.v} onClick={() => setProspectType(t.v)}
                  data-testid={`outreach-bulk-type-${t.v}`}
                  className={`flex-1 py-2 rounded-lg border text-[13px] font-bold transition-colors ${prospectType === t.v ? "border-[#FFC107] text-[#FFC107] bg-[#FFC107]/10" : "border-[#2A2F34] text-[#A1A1AA] hover:text-white"}`}>
                  {t.l}
                </button>
              ))}
            </div>
            <label className="flex items-center gap-2 mb-4 cursor-pointer">
              <input type="checkbox" checked={autoDraft} onChange={(e) => setAutoDraft(e.target.checked)}
                data-testid="outreach-bulk-autodraft"
                className="accent-[#FFC107]" />
              <span className="text-[12.5px] text-[#A1A1AA]">Automatically generate outreach emails after import</span>
            </label>
            <p className="text-[13px] text-[#A1A1AA]">
              Paste rows from ChatGPT, Excel, or Google Sheets. Tab-separated (TSV) or CSV is supported. <b className="text-white">Business Name</b> and <b className="text-white">Email</b> are required.
            </p>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {BULK_COLUMNS.map((c) => (
                <span key={c.key} className="text-[10.5px] px-2 py-0.5 rounded-md border border-[#2A2F34] text-[#A1A1AA]">{c.display}</span>
              ))}
            </div>
            <textarea
              rows={14}
              value={text}
              onChange={(e) => setText(e.target.value)}
              data-testid="outreach-bulk-textarea"
              className="sb-input mt-4 font-mono text-[12px]"
              placeholder={"Business Name\tEmail\tCity\tState\nAcme Corp\tacme@example.com\tJoliet\tIL\n…"}
            />
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={onClose} className="sb-btn-secondary text-[12px]" style={{ padding: "8px 14px" }}>Cancel</button>
              <button type="button" onClick={doPreview} disabled={!text.trim()} data-testid="outreach-bulk-preview" className="sb-btn-primary text-[12px] disabled:opacity-40" style={{ padding: "8px 16px" }}>
                Preview <ArrowRight size={13} />
              </button>
            </div>
          </div>
        )}

        {step === "preview" && (
          <div className="mt-5" data-testid="outreach-bulk-preview-state">
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <span className="text-[11px] uppercase tracking-widest text-[#A1A1AA]">Prospect Type:</span>
              <span data-testid="outreach-bulk-preview-type-label" className={`px-2 py-0.5 rounded-md border text-[11px] font-bold ${prospectType === "operator" ? "border-[#3B82F6]/40 text-[#3B82F6] bg-[#3B82F6]/10" : "border-[#FFC107]/40 text-[#FFC107] bg-[#FFC107]/10"}`}>
                {prospectType === "operator" ? "Operator" : "Business"}
              </span>
              {autoDraft && <span className="text-[11px] text-[#A1A1AA]">· Auto-draft <b className="text-white">ON</b></span>}
            </div>
            <div className="text-[13px] text-[#A1A1AA]">
              Parsed <b className="text-white">{rows.length}</b> row{rows.length === 1 ? "" : "s"} · <span className="text-[#22C55E]">{readyCount} ready</span>. Payload-only duplicates and invalid rows are flagged here; duplicates against existing prospects will be flagged after you click Create.
            </div>
            <div className="mt-4 overflow-x-auto border border-[#2A2F34] rounded-lg">
              <table className="w-full text-[12px]" data-testid="outreach-bulk-preview-table">
                <thead className="bg-[#0B0E11] text-[#A1A1AA] uppercase text-[10.5px]">
                  <tr>
                    <th className="text-left p-2 w-8">#</th>
                    <th className="text-left p-2">Business Name</th>
                    <th className="text-left p-2">Email</th>
                    <th className="text-left p-2">City</th>
                    <th className="text-left p-2">ST</th>
                    <th className="text-left p-2">Industry</th>
                    <th className="text-left p-2">Status</th>
                    <th className="text-right p-2 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr><td colSpan={8} className="p-4 text-center text-[#52525b]">No rows parsed. Go back and check your paste.</td></tr>
                  )}
                  {rows.map((r, i) => {
                    const c = classified[i];
                    return (
                      <tr key={i} className="border-t border-[#2A2F34]">
                        <td className="p-2 text-[#52525b]">{i + 1}</td>
                        <td className="p-2 text-white">{r.business_name || <span className="text-[#EF4444]">—</span>}</td>
                        <td className="p-2 text-[#A1A1AA]">{r.email || <span className="text-[#EF4444]">—</span>}</td>
                        <td className="p-2 text-[#A1A1AA]">{r.city || ""}</td>
                        <td className="p-2 text-[#A1A1AA]">{r.state || ""}</td>
                        <td className="p-2 text-[#A1A1AA]">{r.industry_location_type || ""}</td>
                        <td className="p-2">
                          <span className={`px-2 py-0.5 rounded-md border text-[10.5px] font-bold ${pillFor(c.status)}`} data-testid={`outreach-bulk-status-${i}`}>{c.label}</span>
                        </td>
                        <td className="p-2 text-right">
                          <button onClick={() => removeRow(i)} data-testid={`outreach-bulk-remove-${i}`} className="text-[#52525b] hover:text-[#EF4444]"><X size={14} /></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="mt-5 flex justify-between gap-2 flex-wrap">
              <button type="button" onClick={() => setStep("input")} className="sb-btn-secondary text-[12px]" style={{ padding: "8px 14px" }}>← Back</button>
              <button type="button" onClick={doCreate} disabled={busy || readyCount === 0} data-testid="outreach-bulk-create" className="sb-btn-primary text-[12px] disabled:opacity-40" style={{ padding: "8px 16px" }}>
                {busy ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Create {readyCount} Prospect{readyCount === 1 ? "" : "s"}
              </button>
            </div>
          </div>
        )}

        {step === "result" && result && (
          <div className="mt-5" data-testid="outreach-bulk-result">
            <div className="sb-card p-5 border-[#22C55E]/30" style={{ background: "rgba(34,197,94,.05)" }}>
              <div className="flex items-center gap-2 text-[#22C55E]"><CheckCircle2 size={16} /><b className="text-[14px]">Bulk import complete</b></div>
              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-[12.5px]">
                <div><div className="text-[#A1A1AA] text-[10.5px] uppercase">Created</div><div className="text-[22px] font-black text-[#22C55E]" data-testid="outreach-bulk-created-count">{result.summary.created}</div></div>
                <div><div className="text-[#A1A1AA] text-[10.5px] uppercase">Duplicate</div><div className="text-[22px] font-black text-[#A1A1AA]" data-testid="outreach-bulk-dup-count">{result.summary.duplicate}</div></div>
                <div><div className="text-[#A1A1AA] text-[10.5px] uppercase">Invalid</div><div className="text-[22px] font-black text-[#F59E0B]" data-testid="outreach-bulk-invalid-count">{result.summary.invalid}</div></div>
                <div><div className="text-[#A1A1AA] text-[10.5px] uppercase">Failed</div><div className="text-[22px] font-black text-[#EF4444]" data-testid="outreach-bulk-failed-count">{result.summary.failed}</div></div>
              </div>
              {(result.summary.drafts_generated != null || result.summary.drafts_failed != null) && (
                <div className="mt-4 pt-4 border-t border-[#2A2F34] grid grid-cols-2 gap-3 text-[12.5px]" data-testid="outreach-bulk-drafts-summary">
                  <div><div className="text-[#A1A1AA] text-[10.5px] uppercase">Drafts generated</div><div className="text-[20px] font-black text-[#22C55E]" data-testid="outreach-bulk-drafts-generated">{result.summary.drafts_generated || 0}</div></div>
                  <div><div className="text-[#A1A1AA] text-[10.5px] uppercase">Drafts failed</div><div className="text-[20px] font-black text-[#EF4444]" data-testid="outreach-bulk-drafts-failed">{result.summary.drafts_failed || 0}</div></div>
                </div>
              )}
            </div>
            <details className="mt-4 sb-card p-4">
              <summary className="cursor-pointer text-[12px] text-[#A1A1AA] hover:text-white">Per-row detail ({result.results.length})</summary>
              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-[12px]">
                  <thead className="text-[#A1A1AA] uppercase text-[10.5px]"><tr><th className="text-left p-2 w-10">#</th><th className="text-left p-2">Status</th><th className="text-left p-2">Detail</th></tr></thead>
                  <tbody>
                    {result.results.map((r, i) => (
                      <tr key={i} className="border-t border-[#2A2F34]">
                        <td className="p-2 text-[#52525b]">{(r?.row ?? i) + 1}</td>
                        <td className="p-2"><span className={`px-2 py-0.5 rounded-md border text-[10.5px] font-bold ${resPillFor(r?.status || "failed")}`}>{r?.status || "failed"}</span></td>
                        <td className="p-2 text-[#A1A1AA]">
                          {r?.status === "created" && <span className="text-[#22C55E]">prospect id {String(r.prospect_id).slice(0,8)}…</span>}
                          {r?.status === "duplicate" && <>Email <b className="text-white">{r.email}</b> already exists ({r.scope})</>}
                          {r?.status === "invalid" && <>{(r.errors || []).join(", ")}</>}
                          {r?.status === "failed" && <>{r.error}</>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
            <div className="mt-5 flex justify-end">
              <button type="button" onClick={close} data-testid="outreach-bulk-done" className="sb-btn-primary text-[12px]" style={{ padding: "8px 16px" }}>Close</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── BATCH REVIEW / BULK SEND ────────────────────────────────────────
function BatchReviewModal({ onClose, onComplete }) {
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState([]); // [{ prospect_id, message_id, subject, body, company_name, prospect_type, email }]
  const [approved, setApproved] = useState({}); // { message_id: true }
  const [edits, setEdits] = useState({}); // { message_id: { subject, body } }
  const [expanded, setExpanded] = useState({});
  const [dailyLimit, setDailyLimit] = useState(25);
  const [sentToday, setSentToday] = useState(0);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rDG, rRR, st] = await Promise.all([
        authedFetch("/api/admin/outreach/prospects?status=draft_generated&limit=200").then((r) => r.ok ? r.json() : { rows: [] }),
        authedFetch("/api/admin/outreach/prospects?status=ready_for_review&limit=200").then((r) => r.ok ? r.json() : { rows: [] }),
        authedFetch("/api/admin/outreach/stats").then((r) => r.ok ? r.json() : null),
      ]);
      const rows = [...(rDG.rows || []), ...(rRR.rows || [])];
      const collected = [];
      for (const p of rows.slice(0, 100)) {
        const d = await authedFetch(`/api/admin/outreach/prospects/${p.id}`).then((r) => r.ok ? r.json() : null);
        if (!d) continue;
        const draftMsgs = (d.messages || []).filter((m) => m.status === "draft");
        if (!draftMsgs.length) continue;
        const m = draftMsgs[draftMsgs.length - 1]; // latest
        collected.push({
          prospect_id: p.id, message_id: m.id, subject: m.subject, body: m.body,
          company_name: d.prospect.company_name, prospect_type: d.prospect.prospect_type,
          email: d.prospect.email,
        });
      }
      setDrafts(collected);
      if (st) { setDailyLimit(st.daily_limit || 25); setSentToday(st.sent_today || 0); }
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remaining = Math.max(0, dailyLimit - sentToday);
  const approvedIds = Object.keys(approved).filter((k) => approved[k]);
  const total = drafts.length;
  const overCap = approvedIds.length > remaining;

  function toggleAll() {
    if (approvedIds.length === total) setApproved({});
    else {
      const next = {}; drafts.forEach((d) => { next[d.message_id] = true; });
      setApproved(next);
    }
  }

  async function doSend() {
    if (approvedIds.length === 0) return;
    setSending(true);
    try {
      // Compose overrides map from any inline edits
      const overrides = {};
      for (const mid of approvedIds) {
        const e = edits[mid]; const d = drafts.find((x) => x.message_id === mid);
        if (e && d && (e.subject !== d.subject || e.body !== d.body)) {
          overrides[mid] = { subject: e.subject, body: e.body };
        }
      }
      const idsToSend = approvedIds.slice(0, Math.min(25, remaining || 25));
      const r = await authedFetch("/api/admin/outreach/messages/bulk-send", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message_ids: idsToSend, overrides }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(data.detail || "Bulk send failed"); setSending(false); return; }
      setResult(data);
      setSending(false);
      const sent = data.summary?.sent || 0;
      if (sent > 0) toast.success(`${sent} email${sent === 1 ? "" : "s"} sent`);
    } catch { toast.error("Network error"); setSending(false); }
  }

  function close() {
    if (sending) return;
    if (result && (result.summary?.sent || 0) > 0) onComplete?.();
    else onClose?.();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={close} data-testid="outreach-review-modal">
      <div className="sb-card p-6 w-full max-w-5xl max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <div className="sb-eyebrow">Batch review · Bulk send</div>
            <div className="mt-1 text-[20px] font-black">Review drafts and send</div>
          </div>
          <button type="button" onClick={close} className="text-[#A1A1AA] hover:text-white" data-testid="outreach-review-close"><X size={18} /></button>
        </div>

        {loading && (
          <div className="mt-6 flex items-center gap-2 text-[#A1A1AA] text-[13px]"><Loader2 size={14} className="animate-spin" /> Loading drafts…</div>
        )}

        {!loading && !result && (
          <>
            <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
              <button type="button" onClick={toggleAll} data-testid="outreach-review-select-all" className="sb-btn-secondary text-[11px]" style={{ padding: "6px 12px" }}>
                {approvedIds.length === total && total > 0 ? "Select None" : "Select All"}
              </button>
              <div className="flex items-center gap-3 text-[12px]">
                <span className="text-[#A1A1AA]">Approved: <b className="text-white">{approvedIds.length} / {total}</b></span>
                <span data-testid="outreach-review-daily-cap" className="text-[#A1A1AA]">Daily send limit: <b className="text-white">{dailyLimit}</b> ({remaining} remaining today)</span>
              </div>
            </div>
            {overCap && (
              <div className="mt-3 sb-card p-3 border-[#F59E0B]/40 text-[12px] text-[#F59E0B]" style={{ background: "rgba(245,158,11,.05)" }}>
                <AlertTriangle size={13} className="inline mr-1" /> You&apos;ve approved {approvedIds.length} but only {remaining} can send today. Excess will remain as drafts.
              </div>
            )}
            <div className="mt-4 space-y-2">
              {total === 0 && <div className="sb-card p-8 text-center text-[#52525b] text-[13px]">No drafts ready for review.</div>}
              {drafts.map((d, i) => {
                const isChecked = !!approved[d.message_id];
                const isExp = !!expanded[d.message_id];
                const e = edits[d.message_id] || { subject: d.subject, body: d.body };
                return (
                  <div key={d.message_id} data-testid={`outreach-review-row-${i}`} className="sb-card p-3">
                    <div className="flex items-start gap-3">
                      <input type="checkbox" checked={isChecked}
                        onChange={(ev) => setApproved({ ...approved, [d.message_id]: ev.target.checked })}
                        data-testid={`outreach-review-approve-${i}`}
                        className="mt-1 accent-[#FFC107]" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <b className="text-[13px]">{d.company_name}</b>
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${d.prospect_type === "operator" ? "border border-[#3B82F6]/40 text-[#3B82F6] bg-[#3B82F6]/10" : "border border-[#FFC107]/40 text-[#FFC107] bg-[#FFC107]/10"}`}>{d.prospect_type}</span>
                          <span className="text-[11px] text-[#A1A1AA] truncate">{d.email}</span>
                        </div>
                        <div className="mt-1 text-[12.5px] text-white truncate">{e.subject}</div>
                        {!isExp && <div className="mt-0.5 text-[11.5px] text-[#A1A1AA] truncate">{e.body.slice(0, 120)}…</div>}
                        {isExp && (
                          <div className="mt-2 space-y-2">
                            <input value={e.subject} onChange={(ev) => setEdits({ ...edits, [d.message_id]: { ...e, subject: ev.target.value } })} className="sb-input text-[12.5px]" />
                            <textarea value={e.body} onChange={(ev) => setEdits({ ...edits, [d.message_id]: { ...e, body: ev.target.value } })} rows={8} className="sb-input text-[12px] font-mono" />
                          </div>
                        )}
                      </div>
                      <button type="button" onClick={() => setExpanded({ ...expanded, [d.message_id]: !isExp })} className="text-[11px] text-[#FFC107] hover:underline shrink-0">{isExp ? "Collapse" : "Edit"}</button>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-5 flex justify-between gap-2 flex-wrap">
              <button type="button" onClick={onClose} className="sb-btn-secondary text-[12px]" style={{ padding: "8px 14px" }}>Cancel</button>
              <button type="button" onClick={doSend} disabled={sending || approvedIds.length === 0} data-testid="outreach-review-send-approved" className="sb-btn-primary text-[12px] disabled:opacity-40" style={{ padding: "8px 16px" }}>
                {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Send Approved ({approvedIds.length})
              </button>
            </div>
          </>
        )}

        {result && (
          <div className="mt-5" data-testid="outreach-review-result">
            <div className="sb-card p-5 border-[#22C55E]/30" style={{ background: "rgba(34,197,94,.05)" }}>
              <div className="flex items-center gap-2 text-[#22C55E]"><CheckCircle2 size={16} /><b className="text-[14px]">Bulk send complete</b></div>
              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-[12.5px]">
                <div><div className="text-[#A1A1AA] text-[10.5px] uppercase">Sent</div><div className="text-[22px] font-black text-[#22C55E]" data-testid="outreach-review-sent">{result.summary.sent}</div></div>
                <div><div className="text-[#A1A1AA] text-[10.5px] uppercase">Skipped (cap)</div><div className="text-[22px] font-black text-[#F59E0B]" data-testid="outreach-review-skipped-cap">{result.summary.skipped_daily_limit}</div></div>
                <div><div className="text-[#A1A1AA] text-[10.5px] uppercase">Skipped (suppr.)</div><div className="text-[22px] font-black text-[#A1A1AA]" data-testid="outreach-review-skipped-suppr">{result.summary.skipped_suppressed}</div></div>
                <div><div className="text-[#A1A1AA] text-[10.5px] uppercase">Failed</div><div className="text-[22px] font-black text-[#EF4444]" data-testid="outreach-review-failed">{result.summary.failed}</div></div>
              </div>
            </div>
            <div className="mt-5 flex justify-end">
              <button type="button" onClick={close} className="sb-btn-primary text-[12px]" style={{ padding: "8px 16px" }}>Close</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ProspectDetailModal({ prospectId, gmailConnected, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [goal, setGoal] = useState("initial");
  const [focus, setFocus] = useState("");
  const [reviewMsg, setReviewMsg] = useState(null); // { id, subject, body }
  const [confirmDel, setConfirmDel] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await authedFetch(`/api/admin/outreach/prospects/${prospectId}`);
    if (r.ok) setDetail(await r.json());
    setLoading(false);
  }, [prospectId]);

  useEffect(() => { load(); }, [load]);

  async function generate() {
    setGenerating(true);
    try {
      const r = await authedFetch(`/api/admin/outreach/prospects/${prospectId}/generate`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ goal, focus }),
      });
      const data = await r.json().catch(() => ({}));
      if (r.status === 503) { toast.error("OPENAI_API_KEY not configured on the server."); return; }
      if (!r.ok) { toast.error(data.detail || data.error || "Generation failed."); return; }
      toast.success("Draft generated");
      setReviewMsg({ id: data.message_id, subject: data.subject, body: data.body });
      load();
    } finally { setGenerating(false); }
  }

  async function markStatus(newStatus) {
    const body = { status: newStatus };
    if (newStatus === "do_not_contact") body.do_not_contact = true;
    const r = await authedFetch(`/api/admin/outreach/prospects/${prospectId}`, {
      method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
    });
    if (r.ok) { toast.success("Updated"); load(); }
    else { toast.error("Update failed"); }
  }

  async function deleteProspect() {
    const r = await authedFetch(`/api/admin/outreach/prospects/${prospectId}`, { method: "DELETE" });
    if (r.ok) { toast.success("Prospect deleted"); onClose(); }
    else { toast.error("Delete failed"); setConfirmDel(false); }
  }

  if (loading || !detail) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
        <div className="sb-card p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-3 text-[#A1A1AA]"><Loader2 size={16} className="animate-spin" /> Loading prospect…</div>
        </div>
      </div>
    );
  }

  const p = detail.prospect;
  const drafts = (detail.messages || []).filter((m) => m.status === "draft");

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose} data-testid="outreach-detail-modal">
        <div className="sb-card p-6 w-full max-w-3xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="sb-eyebrow">Prospect</div>
              <div className="mt-1 text-[22px] font-black">{p.company_name}</div>
              <div className="text-[13px] text-[#A1A1AA] mt-1">
                {p.contact_name && <>{p.contact_name} · </>}<span className="break-all">{p.email}</span>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Badge status={p.status} />
                <span className="text-[10.5px] uppercase tracking-widest text-[#A1A1AA]">{p.prospect_type}</span>
                {p.city && <span className="text-[11px] text-[#A1A1AA]">· {p.city}{p.state ? `, ${p.state}` : ""}</span>}
              </div>
            </div>
            <button onClick={onClose} className="text-[#A1A1AA] hover:text-white" data-testid="outreach-detail-close"><X size={18} /></button>
          </div>

          {/* Action buttons */}
          <div className="mt-5 flex flex-wrap gap-2">
            <button onClick={() => markStatus("interested")} className="sb-btn-secondary text-[11px]" style={{ padding: "7px 12px" }} data-testid="outreach-mark-interested"><CheckCircle2 size={12} /> Interested</button>
            <button onClick={() => markStatus("registered")} className="sb-btn-secondary text-[11px]" style={{ padding: "7px 12px" }} data-testid="outreach-mark-registered">Registered</button>
            <button onClick={() => markStatus("not_interested")} className="sb-btn-secondary text-[11px]" style={{ padding: "7px 12px" }} data-testid="outreach-mark-not">Not Interested</button>
            <button onClick={() => markStatus("do_not_contact")} className="text-[11px] font-bold px-3 py-2 rounded-lg border border-[#EF4444]/50 text-[#EF4444] hover:bg-[#EF4444]/10" data-testid="outreach-mark-dnc"><ShieldOff size={12} className="inline mr-1" /> Do Not Contact</button>
            <div className="flex-1" />
            <button onClick={() => setConfirmDel(true)} className="inline-flex items-center gap-2 text-[11px] font-bold px-3 py-2 rounded-lg border border-[#EF4444]/50 text-[#EF4444] hover:bg-[#EF4444]/10" data-testid="outreach-delete-prospect"><Trash2 size={12} /> Delete</button>
          </div>

          {/* Fields */}
          <div className="mt-6 grid sm:grid-cols-2 gap-x-6 divide-y sm:divide-y-0 divide-[#2A2F34]">
            {[
              ["phone", p.phone], ["website", p.website],
              ["zip", p.zip], ["industry / location", p.industry_location_type],
              ["source", p.source], ["source url", p.source_url],
              ["last contact", p.last_contact_at ? new Date(p.last_contact_at).toLocaleString() : "—"],
              ["next follow-up", p.next_follow_up_at ? new Date(p.next_follow_up_at).toLocaleString() : "—"],
              ["notes", p.notes],
            ].map(([k, v]) => (
              <div key={k} className="py-2 flex items-start gap-3 text-[12.5px]">
                <div className="w-40 shrink-0 text-[#A1A1AA] sb-eyebrow" style={{ fontSize: 10 }}>{k}</div>
                <div className="flex-1 text-white/90 break-all">{v || <span className="text-[#52525b]">—</span>}</div>
              </div>
            ))}
          </div>

          {/* Generate draft */}
          <div className="mt-6 sb-card p-4" style={{ background: "rgba(255,193,7,.04)" }}>
            <div className="sb-eyebrow flex items-center gap-2"><Sparkles size={12} className="text-[#FFC107]" /> Generate Email</div>
            <div className="mt-3 flex flex-wrap gap-2 items-end">
              <div>
                <div className="sb-label">Goal</div>
                <select className="sb-input" value={goal} onChange={(e) => setGoal(e.target.value)} data-testid="outreach-goal">
                  <option value="initial">Initial</option>
                  <option value="follow_up">Follow-up</option>
                  <option value="final_follow_up">Final Follow-up</option>
                </select>
              </div>
              <div className="flex-1 min-w-[220px]">
                <div className="sb-label">Focus (optional)</div>
                <input className="sb-input" value={focus} onChange={(e) => setFocus(e.target.value)} placeholder="e.g. 24/7 warehouse crew, 3 shifts" data-testid="outreach-focus" />
              </div>
              <button onClick={generate} disabled={generating} data-testid="outreach-generate" className="sb-btn-primary text-[11px]" style={{ padding: "10px 14px" }}>
                {generating ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Generate
              </button>
            </div>
          </div>

          {/* Drafts */}
          {drafts.length > 0 && (
            <div className="mt-5">
              <div className="sb-eyebrow">Drafts</div>
              <div className="mt-2 divide-y divide-[#2A2F34]">
                {drafts.map((m) => (
                  <div key={m.id} className="py-2 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-bold text-[13px] truncate">{m.subject}</div>
                      <div className="text-[11px] text-[#A1A1AA]">{m.message_type} · {m.generated_by_ai ? m.ai_model : "manual"} · {new Date(m.created_at).toLocaleString()}</div>
                    </div>
                    <button onClick={() => setReviewMsg({ id: m.id, subject: m.subject, body: m.body })} data-testid={`outreach-open-draft-${m.id}`} className="sb-btn-secondary text-[11px]" style={{ padding: "7px 12px" }}>Review &amp; Send</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Sent messages */}
          {(detail.messages || []).filter((m) => m.status !== "draft").length > 0 && (
            <div className="mt-5">
              <div className="sb-eyebrow">Messages</div>
              <div className="mt-2 divide-y divide-[#2A2F34]">
                {(detail.messages || []).filter((m) => m.status !== "draft").map((m) => (
                  <div key={m.id} className="py-2 text-[12.5px]">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-bold truncate">{m.subject}</div>
                      <span className={`text-[10.5px] font-bold uppercase tracking-wider ${m.status === "sent" ? "text-[#22C55E]" : "text-[#EF4444]"}`}>{m.status}</span>
                    </div>
                    <div className="text-[11px] text-[#A1A1AA]">{m.message_type} · {m.sent_at ? new Date(m.sent_at).toLocaleString() : "—"}</div>
                    {m.error_summary && <div className="text-[11px] text-[#EF4444] mt-1">{m.error_summary}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Activity feed */}
          {(detail.events || []).length > 0 && (
            <div className="mt-5">
              <div className="sb-eyebrow">Activity</div>
              <div className="mt-2 divide-y divide-[#2A2F34]">
                {detail.events.slice(0, 20).map((e, i) => (
                  <div key={i} className="py-2 text-[11.5px] text-[#A1A1AA]">
                    <span className="text-white font-bold mr-2">{e.event_type}</span>
                    {new Date(e.created_at).toLocaleString()}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {reviewMsg && (
        <ReviewSendModal
          prospectId={prospectId}
          prospectEmail={p.email}
          gmailConnected={gmailConnected}
          suppressed={p.do_not_contact}
          initialMsg={reviewMsg}
          onClose={() => setReviewMsg(null)}
          onSent={() => { setReviewMsg(null); load(); }}
        />
      )}

      {confirmDel && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4" onClick={() => setConfirmDel(false)}>
          <div className="sb-card p-6 w-full max-w-md border-[#EF4444]/40" onClick={(e) => e.stopPropagation()} data-testid="outreach-delete-confirm">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#EF4444]/10 text-[#EF4444] flex items-center justify-center shrink-0"><AlertTriangle size={18} /></div>
              <div><div className="font-black">Permanently delete this prospect?</div><div className="mt-1 text-[13px] text-[#A1A1AA]">This deletes drafts, sent messages, and activity. Cannot be undone.</div></div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setConfirmDel(false)} className="sb-btn-secondary text-[12px]" style={{ padding: "8px 14px" }}>Cancel</button>
              <button onClick={deleteProspect} data-testid="outreach-delete-confirm-submit" className="text-[12px] font-bold px-4 py-2 rounded-lg bg-[#EF4444] text-white hover:bg-[#DC2626]"><Trash2 size={13} className="inline mr-1" /> Delete Permanently</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ReviewSendModal({ prospectId, prospectEmail, gmailConnected, initialMsg, onClose, onSent }) {
  const [subject, setSubject] = useState(initialMsg.subject);
  const [body, setBody] = useState(initialMsg.body);
  const [followDays, setFollowDays] = useState(5);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [confirm, setConfirm] = useState(false);

  async function doSend() {
    setBusy(true); setErr("");
    try {
      const r = await authedFetch(`/api/admin/outreach/prospects/${prospectId}/send`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message_id: initialMsg.id,
          subject_override: subject,
          body_override: body,
          next_follow_up_days: followDays || 0,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (r.status === 503 && data.error === "gmail_oauth_not_configured") {
        setErr("Gmail is not connected. Connect Gmail from the Outreach page first."); setBusy(false); return;
      }
      if (r.status === 401 && data.error === "gmail_reauth_required") {
        setErr("Gmail authorization expired. Reconnect Gmail."); setBusy(false); return;
      }
      if (r.status === 429) { setErr(data.detail || "Daily send limit reached."); setBusy(false); return; }
      if (r.status === 409) { setErr(data.detail || "Blocked."); setBusy(false); return; }
      if (!r.ok) { setErr(data.error_summary || data.detail || data.error || "Send failed."); setBusy(false); return; }
      toast.success("Email sent");
      onSent?.();
    } catch { setErr("Network error."); setBusy(false); }
  }

  const fromDisplay = "Tomas from SpotBounty <tomas@spotbounty.com>";

  return (
    <div className="fixed inset-0 z-[55] flex items-center justify-center bg-black/70 p-4" onClick={() => !busy && onClose()} data-testid="outreach-single-review-modal">
      <div className="sb-card p-6 w-full max-w-2xl max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div><div className="sb-eyebrow">Review &amp; Send</div><div className="mt-1 text-[18px] font-black">Draft email</div></div>
          <button onClick={onClose} className="text-[#A1A1AA] hover:text-white"><X size={18} /></button>
        </div>

        <div className="mt-4 space-y-3">
          <div>
            <div className="sb-label">From</div>
            <input disabled className="sb-input opacity-70" value={fromDisplay} readOnly />
          </div>
          <div>
            <div className="sb-label">To</div>
            <input disabled className="sb-input opacity-70" value={prospectEmail} readOnly data-testid="outreach-review-to" />
          </div>
          <div>
            <div className="sb-label">Subject</div>
            <input className="sb-input" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="outreach-review-subject" />
          </div>
          <div>
            <div className="sb-label">Body</div>
            <textarea rows={15} className="sb-input sb-mono" value={body} onChange={(e) => setBody(e.target.value)} data-testid="outreach-review-body" />
            <div className="mt-1 text-[10.5px] text-[#52525b]">{body.length} chars · min 20, max 5000</div>
          </div>
          <div>
            <div className="sb-label">Follow up in</div>
            <div className="flex flex-wrap gap-2">
              {[3, 5, 7, 0].map((d) => (
                <button key={d} type="button" onClick={() => setFollowDays(d)}
                  className={`text-[11px] font-bold px-3 py-2 rounded-lg border ${followDays === d ? "border-[#FFC107] text-[#FFC107] bg-[#FFC107]/10" : "border-[#2A2F34] text-[#A1A1AA] hover:text-white"}`}
                  data-testid={`outreach-followup-${d}`}>
                  {d === 0 ? "None" : `${d} days`}
                </button>
              ))}
            </div>
          </div>
        </div>

        {!gmailConnected && <div className="mt-4 text-[12px] text-[#F59E0B]">Gmail is not connected. Connect Gmail on the Outreach page before sending.</div>}
        {err && <div className="mt-4 text-[12px] text-[#EF4444]" data-testid="outreach-review-error">{err}</div>}

        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} disabled={busy} className="sb-btn-secondary text-[12px]" style={{ padding: "8px 14px" }}>Back</button>
          <button onClick={() => setConfirm(true)} disabled={busy || !gmailConnected} data-testid="outreach-review-send"
            className="sb-btn-primary text-[12px]" style={{ padding: "8px 16px" }}>
            <Send size={13} /> Send Email
          </button>
        </div>

        {confirm && (
          <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4" onClick={() => !busy && setConfirm(false)}>
            <div className="sb-card p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="outreach-send-confirm">
              <div className="font-black">Send this email to <span className="sb-mono">{prospectEmail}</span>?</div>
              <div className="mt-1 text-[13px] text-[#A1A1AA]">This will send from your Gmail.</div>
              <div className="mt-5 flex justify-end gap-2">
                <button onClick={() => setConfirm(false)} disabled={busy} className="sb-btn-secondary text-[12px]" style={{ padding: "8px 14px" }}>Cancel</button>
                <button onClick={() => { setConfirm(false); doSend(); }} disabled={busy} data-testid="outreach-send-confirm-submit" className="sb-btn-primary text-[12px]" style={{ padding: "8px 16px" }}>
                  {busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Send
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

